import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Gift, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../contexts/LanguageContext";
import { waLink } from "../lib/contact";

export default function Pricing({ onOpenLead }) {
  const { t } = useLang();
  return (
    <section id="pricing" className="py-20 md:py-28 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">{t.pricing.eyebrow}</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 text-slate-950">{t.pricing.title}</h2>
          <p className="text-slate-600 mt-4 text-base md:text-lg">{t.pricing.subtitle}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 lg:gap-7">
          {t.pricing.tiers.map((tier, i) => {
            const popular = i === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                className={`relative rounded-3xl p-7 md:p-8 transition flex flex-col ${
                  popular
                    ? "bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white border border-blue-500/30 shadow-[0_30px_80px_-20px_rgba(59,130,246,0.5)] md:-translate-y-3"
                    : "bg-white border border-slate-200 hover:border-slate-300 hover:shadow-xl"
                }`}
                data-testid={`pricing-tier-${i}`}
              >
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-lg">
                    <Sparkles className="w-3 h-3" /> {t.pricing.popular}
                  </div>
                )}

                <div className={popular ? "text-blue-300" : "text-blue-600"}>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em]">{tier.tag}</div>
                </div>
                <h3 className={`font-display text-2xl font-bold mt-2 ${popular ? "text-white" : "text-slate-950"}`}>
                  {tier.name}
                </h3>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className={`font-display text-5xl font-bold ${popular ? "text-white" : "text-slate-950"}`}>
                    {tier.price}
                  </span>
                  <span className={`text-sm ${popular ? "text-slate-400" : "text-slate-500"}`}>
                    {t.pricing.monthly}
                  </span>
                </div>

                {/* Urgency banner */}
                {tier.urgency && (
                  <div className={`mt-5 rounded-xl p-3 border-2 border-dashed flex items-start gap-2.5 ${
                    popular
                      ? "border-amber-400/40 bg-amber-400/10"
                      : "border-amber-300 bg-amber-50"
                  }`}>
                    <Gift className={`w-4 h-4 mt-0.5 flex-shrink-0 ${popular ? "text-amber-300" : "text-amber-600"}`} />
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-wider ${popular ? "text-amber-300" : "text-amber-700"}`}>
                        {t.pricing.urgencyBadge}
                      </div>
                      <div className={`text-[13px] font-semibold mt-0.5 leading-snug ${popular ? "text-white" : "text-slate-900"}`}>
                        {tier.urgency}
                      </div>
                    </div>
                  </div>
                )}

                <ul className={`mt-6 space-y-3 flex-1 ${popular ? "text-slate-200" : "text-slate-700"}`}>
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-[15px]">
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          popular ? "bg-emerald-500/20 border border-emerald-400/40" : "bg-emerald-50 border border-emerald-200"
                        }`}
                      >
                        <Check className={`w-3 h-3 ${popular ? "text-emerald-300" : "text-emerald-600"}`} strokeWidth={3} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {tier.fineprint && (
                  <div className={`mt-4 text-[11px] leading-relaxed ${popular ? "text-slate-400" : "text-slate-400"}`}>
                    {tier.fineprint}
                  </div>
                )}

                <Button
                  onClick={() => onOpenLead(tier.name)}
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-6 w-full h-12 rounded-full font-semibold ${
                    popular
                      ? "bg-white text-slate-950 hover:bg-white/90"
                      : "bg-slate-950 text-white hover:bg-slate-800"
                  }`}
                >
                  {t.pricing.cta}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Custom plan card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative mt-14 rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50/40"
          data-testid="pricing-custom"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-blue-200/50 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-violet-200/40 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 p-8 md:p-12 items-center">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-violet-700">
                {t.pricing.custom.eyebrow}
              </div>
              <h3 className="font-display text-2xl md:text-4xl font-bold mt-3 text-slate-950 leading-tight">
                {t.pricing.custom.title}
              </h3>
              <p className="text-slate-600 mt-3 text-base">{t.pricing.custom.sub}</p>

              <ul className="mt-5 space-y-2">
                {t.pricing.custom.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-700" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3 md:items-end">
              <Button
                onClick={() => onOpenLead("Custom")}
                data-testid="pricing-custom-cta"
                className="h-13 px-7 py-6 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base hover:opacity-95 shadow-lg shadow-blue-500/30 w-full md:w-auto"
              >
                {t.pricing.custom.cta}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                href={waLink("Hi Launch Mitra, I want a custom plan for my business.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-custom-whatsapp"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-slate-300 hover:border-slate-400 bg-white text-slate-900 font-semibold text-sm w-full md:w-auto"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
